// components/LanguageDashboard.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { jwtDecode } from "jwt-decode";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Container,
  Avatar,
  CircularProgress,
  Collapse,
  Grid,
  Paper,
  IconButton,
  useMediaQuery
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowRightIcon,
  ExpandMore as ChevronDownIcon,
  ExpandLess as ChevronUpIcon,
  OpenInNew as ExternalLinkIcon,
  SportsEsports as GamesIcon,
  Chat as ChatIcon,
  Explore as ExploreIcon,
  EmojiEvents as AchievementsIcon,
  School as School,
  Assessment as AssessmentIcon
} from '@mui/icons-material';

import LanguageDashboardHeader from './LanguageDashboardHeaderComponent';
import LearningGoalCard from './LearningGoalCard';
import CourseProgressComponent from './CourseProgressComponent';
import FlashcardProgressCard from './FlashcardProgressCard';

import languageLearningApi from '../middleware/languageLearningApi';
import api from '../../../api';
import CardPreprocessor from '../../../utils/CardPreprocessor';
import { useTheme } from '@mui/material/styles';
import UserStreakComponent from './UserStreakComponent';
import UserLevelProgress from './UserLevelProgress';

// Tour imports - Updated paths for tour/ folder
import TourOverlay from '../../tour/TourOverlay';
import { useTourManager } from '../../tour/TourManager';

const LanguageDashboard = ({ course, token }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState(null);
  const [username, setUsername] = useState('');
  const [courseData, setCourseData] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [courselets, setCourselets] = useState([]);
  const [reviewCards, setReviewCards] = useState([]);
  const [reviewCount, setReviewCount] = useState(0);

  // Section expansion states
  const [journeySectionExpanded, setJourneySectionExpanded] = useState(true);
  const [funSectionExpanded, setFunSectionExpanded] = useState(true);

  // Review metrics
  const [reviewMetrics, setReviewMetrics] = useState({
    sentenceCount: 0,
    wordCount: 0,
    shadowingCount: 0,
    totalCount: 0
  });

  // Extract userId from token
  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        console.log('Decoded token:', decoded);
        // Ensure the structure is correct
        if (decoded && decoded.user && decoded.user._id) {
          setUserId(decoded.user._id);
          setUsername(decoded.user.username || '');
        } else {
          console.error('Decoded token does not contain expected user._id:', decoded);
        }
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
  }, [token]);

  // Fetch course data, progress data, and review count
  useEffect(() => {
    const fetchAllData = async () => {
      console.log('Starting fetchAllData', { userId, course });
      if (!userId || !course?._id) {
        console.warn('Missing required data, aborting fetch:', { userId, courseId: course?.courseId, course_id: course?._id });
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Fetch course data to get the deckId
        console.log('Fetching course data for course id:', course._id);
        const courseResponse = await languageLearningApi.get(`/courses/${course._id}`);
        console.log('Course response:', courseResponse.data);
        setCourseData(courseResponse.data.course);
        const deckId = courseResponse.data.course.deckId;
        console.log('Extracted deckId:', deckId);

        // Fetch progress data using new endpoint
        console.log('Fetching progress data for user:', userId, 'and course:', course._id);
        const progressResponse = await languageLearningApi.get(`/progress/${userId}/${course._id}`);
        console.log('Progress response:', progressResponse.data);
        if (progressResponse.data.success) {
          const userProgress = progressResponse.data.data;
          setProgressData(userProgress);

          // Fetch detailed data for each courselet
          console.log('Fetching details for active courselets:', userProgress.activeCourselets);
          const activePromises = userProgress.activeCourselets.map(courseletId =>
            languageLearningApi.get(`/courselets/${courseletId}/details`)
          );

          console.log('Fetching details for completed courselets:', userProgress.completedCourselets);
          const completedPromises = userProgress.completedCourselets.map(courseletId =>
            languageLearningApi.get(`/courselets/${courseletId}/details`)
          );

          const activeResults = await Promise.all(activePromises);
          const completedResults = await Promise.all(completedPromises);

          const activeCourselets = activeResults.map(res => {
            console.log('Active courselet detail:', res.data.courselet);
            return {
              ...res.data.courselet,
              status: 'active'
            };
          });

          const completedCourselets = completedResults.map(res => {
            console.log('Completed courselet detail:', res.data.courselet);
            return {
              ...res.data.courselet,
              status: 'completed'
            };
          });

          setCourselets([...activeCourselets, ...completedCourselets]);
        } else {
          console.error('Progress response indicated failure:', progressResponse.data);
        }

        // Fetch review count and cards if deckId exists
        if (deckId) {
          try {
            console.log('Fetching review count for deck:', deckId);
            const reviewResponse = await api.get(`/decks/${deckId}/review`);
            console.log('Review response:', reviewResponse.data);
            const dueCards = reviewResponse.data.dueCards || [];
            setReviewCards(dueCards);
            setReviewCount(dueCards.length);

            // Calculate review metrics
            const metrics = CardPreprocessor.analyzeReviewCards(dueCards);
            setReviewMetrics(metrics);
          } catch (error) {
            console.error('Error fetching review count:', error);
            setReviewCards([]);
            setReviewCount(0);
          }
        } else {
          console.warn('No deckId available from course data.');
        }

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        console.log('Finished fetchAllData, setting loading to false');
        setLoading(false);
      }
    };

    fetchAllData();
  }, [userId, course?._id, course?.courseId]);

  // Toggle section expansion
  const toggleJourneySection = () => setJourneySectionExpanded(!journeySectionExpanded);

  // Navigation functions
  const startReview = () => {
    if (courseData?.deckId && reviewCount > 0) {
      navigate(`/review-adapter/${courseData.deckId}`);
    }
  };

  const navigateToLearn = () => {
    if (courseData?._id) {
      navigate(`/course-learn`);
    }
  };

  const navigateToGames = () => {
    navigate('/games');
  };

  const navigateToChat = () => {
    if (courseData?.language) {
      navigate(`/chat/${courseData.language}`);
    } else {
      navigate('/chat');
    }
  };

  // Calculate statistics
  const activeCount = courselets.filter(c => c.status === 'active').length;
  const completedCount = courselets.filter(c => c.status === 'completed').length;
  const totalCourselets = activeCount + completedCount;
  const progressPercentage = totalCourselets > 0
    ? Math.round((completedCount / totalCourselets) * 100)
    : 0;

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading your language dashboard...
        </Typography>
      </Container>
    );
  }

  // Custom card styles
  const cardStyle = {
    borderRadius: 2,
    boxShadow: 3,
    height: '100%',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: 6,
    }
  };

  const sectionHeadingStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    mb: 2,
    pb: 1,
    borderBottom: `1px solid ${theme.palette.divider}`
  };

  return (
    <Container maxWidth="xl" sx={{ mt: -2 }}>
      <TourManagerWrapper />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header with course info */}
        <LanguageDashboardHeader
          username={username}
          courseTitle={course.title}
          course={courseData}
          userId={userId}
        />

        {/* Learning Journey Section */}
        <Paper
          elevation={0}
          sx={{
            mb: 4,
            p: 2,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <Collapse in={journeySectionExpanded} timeout="auto">
            <Grid container spacing={1} alignItems="stretch" sx={{ minHeight: '500px' }}>
              {/* Column 1: Learning Progress */}
              <Grid item xs={12} lg={5} sx={{
                height: '500px',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: '4px',
                },
              }}>
                <Grid container spacing={2} direction="column">
                  {/* User Level Progress Card */}
                  <Grid item>
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.1 }}
                    >
                      <div data-tour="level-progress">
                        {userId && courseData?.deckId ? (
                          <UserLevelProgress
                            userId={userId}
                            deckId={courseData.deckId}
                            cardStyle={{
                              ...cardStyle,
                              cursor: 'default',
                              minHeight: '120px',
                              boxShadow: 4,
                              transform: 'perspective(1000px) rotateX(0deg)',
                              '&:hover': {
                                transform: 'perspective(1000px) rotateX(2deg) translateY(-5px)',
                                boxShadow: 8,
                              }
                            }}
                          />
                        ) : (
                          <Card sx={{
                            ...cardStyle,
                            minHeight: '120px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <CardContent>
                              <CircularProgress size={24} />
                              <Typography variant="body2" sx={{ mt: 2 }}>
                                Loading level data...
                              </Typography>
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    </motion.div>
                  </Grid>

                  {/* Learning Progress Card */}
                  <Grid item>
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.2 }}
                    >
                      <div data-tour="learning-progress">
                        {userId && course?._id ? (
                          <CourseProgressComponent
                            userId={userId}
                            courseId={course._id}
                            cardStyle={{
                              ...cardStyle,
                              cursor: 'auto',
                              minHeight: '305px',
                              height: '305px',
                              maxHeight: '305px',
                              boxShadow: 4,
                              transform: 'perspective(1000px) rotateX(0deg)',
                              '&:hover': {
                                transform: 'perspective(1000px) rotateX(2deg) translateY(-5px)',
                                boxShadow: 8,
                              }
                            }}
                          />
                        ) : (
                          <Card sx={{
                            ...cardStyle,
                            cursor: 'pointer',
                            minHeight: '120px',
                            maxHeight: '200px',
                            boxShadow: 4,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <CardContent>
                              <CircularProgress size={24} />
                              <Typography variant="body2" sx={{ mt: 2 }}>
                                Loading progress data...
                              </Typography>
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    </motion.div>
                  </Grid>
                </Grid>
              </Grid>

              {/* Column 2: Learning and Review Section */}
              <Grid item xs={12} lg={4} sx={{
                height: '500px',
                overflowY: 'auto',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: '4px',
                },
              }}>
                <Grid container spacing={2} direction="column">
                  <Grid item>
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      <div data-tour="start-learning">
                        <LearningGoalCard
                          userId={userId}
                          courseId={courseData?._id}
                          cardStyle={{
                            ...cardStyle,
                            bgcolor: theme.palette.background.paper,
                            height: '230px',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: 4,
                            transform: 'perspective(1000px) rotateX(0deg)',
                            '&:hover': {
                              transform: 'perspective(1000px) rotateX(2deg) translateY(-5px)',
                              boxShadow: 8,
                            }
                          }}
                          onClick={navigateToLearn}
                        />
                      </div>
                    </motion.div>
                  </Grid>

                  {/* Review Session Card */}
                  <Grid item>
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.35 }}
                    >
                      <div data-tour="review-session">
                        <Card
                          sx={{
                            ...cardStyle,
                            bgcolor: theme.palette.background.paper,
                            height: '230px',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: 4,
                            transform: 'perspective(1000px) rotateX(0deg)',
                            '&:hover': {
                              transform: 'perspective(1000px) rotateX(2deg) translateY(-5px)',
                              boxShadow: 8,
                            }
                          }}
                          onClick={startReview}
                        >
                          <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                              <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                                <PlayIcon fontSize="small" />
                              </Avatar>
                              <Typography variant="h6" fontWeight="600">
                                Review Session
                              </Typography>
                            </Box>

                            <Box sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 2
                            }}>
                              <Typography variant="body2" color="text.secondary">
                                Lock in what you've learned through spaced repetition review.
                              </Typography>

                              <Box sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                bgcolor: theme.palette.primary.light,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: `3px solid ${theme.palette.primary.main}`,
                                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                                ml: 2
                              }}>
                                <Typography variant="h5" fontWeight="600" color="white">
                                  {reviewMetrics.totalCount}
                                </Typography>
                              </Box>
                            </Box>

                            <Button
                              variant="contained"
                              color="primary"
                              size="large"
                              disabled={reviewCount === 0}
                              startIcon={<PlayIcon />}
                              sx={{
                                fontWeight: 600,
                                py: 1.5,
                                width: '100%',
                                mt: 'auto'
                              }}
                            >
                              {reviewCount > 0 ? 'Begin Review' : 'No Cards to Review'}
                            </Button>
                          </CardContent>
                        </Card>
                      </div>
                    </motion.div>
                  </Grid>
                </Grid>
              </Grid>

              {/* Column 3: User Streak Component */}
              <Grid item xs={12} lg={3} sx={{
                height: '500px',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: '4px',
                },
              }}>
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.25 }}
                  style={{ height: '100%' }}
                >
                  <div data-tour="streaks">
                    {userId && courseData?._id ? (
                      <UserStreakComponent
                        userId={userId}
                        courseId={courseData._id}
                        cardStyle={{
                          ...cardStyle,
                          cursor: 'auto',
                          height: '480px',
                          boxShadow: 4,
                          transform: 'perspective(1000px) rotateX(0deg)',
                          '&:hover': {
                            transform: 'perspective(1000px) rotateX(2deg) translateY(-5px)',
                            boxShadow: 8,
                          }
                        }}
                      />
                    ) : (
                      <Card
                        sx={{
                          ...cardStyle,
                          height: '480px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                      >
                        <CardContent>
                          <CircularProgress size={24} />
                          <Typography variant="body2" sx={{ mt: 2 }}>
                            Loading streak data...
                          </Typography>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </motion.div>
              </Grid>
            </Grid>
          </Collapse>
        </Paper>

        {/* Learning Summary and Fun Section */}
        <Paper
          elevation={0}
          sx={{
            mb: 4,
            p: 2,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`
          }}
        >
          <Grid container spacing={3}>
            {/* Left Section - Learning Summary and Stats (70%) */}
            <Grid item xs={12} lg={8.4}>
              <Box sx={sectionHeadingStyle}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: theme.palette.secondary.light, mr: 2 }}>
                    <AssessmentIcon />
                  </Avatar>
                  <Typography variant="h5" fontWeight="600">
                    Learning Summary and Stats
                  </Typography>
                </Box>
              </Box>
              
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <div data-tour="learning-summary">
                  <FlashcardProgressCard token={token} />
                </div>
              </motion.div>
            </Grid>

            {/* Right Section - Fun (30%) */}
            <Grid item xs={12} lg={3.6}>
              <Box sx={sectionHeadingStyle}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: theme.palette.primary.light, mr: 2, width: 32, height: 32 }}>
                    <GamesIcon fontSize="small" />
                  </Avatar>
                  <Typography variant="h5" fontWeight="600">
                    Fun
                  </Typography>
                </Box>
              </Box>

              <Collapse in={funSectionExpanded} timeout="auto">
                <div data-tour="games-chat">
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 2,
                    alignItems: 'center'
                  }}>
                    {/* Games Card - Compact */}
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.45 }}
                      style={{ width: '80%' }}
                    >
                      <Card
                        sx={{
                          ...cardStyle,
                          background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                          height: '140px',
                          width: '100%',
                          boxShadow: 3,
                          transform: 'perspective(1000px) rotateX(0deg)',
                          '&:hover': {
                            transform: 'perspective(1000px) rotateX(2deg) translateY(-3px)',
                            boxShadow: 6,
                          }
                        }}
                        onClick={navigateToGames}
                      >
                        <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.3)', mr: 1.5, width: 32, height: 32 }}>
                              <GamesIcon fontSize="small" />
                            </Avatar>
                            <Typography variant="subtitle1" fontWeight="600" color="white">
                              Games
                            </Typography>
                          </Box>

                          <Typography variant="body2" color="white" sx={{ mb: 2, opacity: 0.9, flexGrow: 1, fontSize: '0.8rem', textAlign: 'center' }}>
                            Play fun games to reinforce learning!
                          </Typography>

                          <Button
                            variant="contained"
                            size="small"
                            sx={{
                              bgcolor: theme.palette.primary.dark,
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              py: 0.5,
                              px: 2,
                              '&:hover': {
                                bgcolor: theme.palette.primary.darker || theme.palette.primary.dark,
                              },
                              width: '35%',
                              minWidth: '60px'
                            }}
                          >
                            PLAY
                          </Button>
                        </CardContent>
                      </Card>
                    </motion.div>

                    {/* Conversation Practice Card - Compact */}
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.5 }}
                      style={{ width: '80%' }}
                    >
                      <Card
                        sx={{
                          ...cardStyle,
                          background: `linear-gradient(135deg, ${theme.palette.secondary.light}, ${theme.palette.secondary.main})`,
                          height: '140px',
                          width: '100%',
                          boxShadow: 3,
                          transform: 'perspective(1000px) rotateX(0deg)',
                          '&:hover': {
                            transform: 'perspective(1000px) rotateX(2deg) translateY(-3px)',
                            boxShadow: 6,
                          }
                        }}
                        onClick={navigateToChat}
                      >
                        <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.3)', mr: 1.5, width: 32, height: 32 }}>
                              <ChatIcon fontSize="small" />
                            </Avatar>
                            <Typography variant="subtitle1" fontWeight="600" color="white">
                              AI Chat
                            </Typography>
                          </Box>

                          <Typography variant="body2" color="white" sx={{ mb: 2, opacity: 0.9, flexGrow: 1, fontSize: '0.8rem', textAlign: 'center' }}>
                            Chat with AI for real life scenarios
                          </Typography>

                          <Button
                            variant="contained"
                            size="small"
                            sx={{
                              bgcolor: theme.palette.secondary.dark,
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              py: 0.5,
                              px: 2,
                              '&:hover': {
                                bgcolor: theme.palette.secondary.darker || theme.palette.secondary.dark,
                              },
                              width: '35%',
                              minWidth: '60px'
                            }}
                          >
                            CHAT
                          </Button>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Box>
                </div>
              </Collapse>
            </Grid>
          </Grid>
        </Paper>
      </motion.div>
      <TourOverlay />
    </Container>
  );
};

// Wrapper component to handle tour initialization - Updated to prevent conflicts
const TourManagerWrapper = () => {
  const { initializeNewUserTour, isActive } = useTourManager();

  useEffect(() => {
    console.log('🎯 TourManagerWrapper: Component mounted, isActive:', isActive);
    
    // Only initialize if tour is not already active
    if (!isActive) {
      console.log('🎬 TourManagerWrapper: Initializing auto-tour check...');
      initializeNewUserTour();
    } else {
      console.log('🚫 TourManagerWrapper: Tour already active, skipping auto-init');
    }
  }, []); // Empty dependency array - only run once when component mounts

  return null;
};

export default LanguageDashboard;